import "./App.css";
import CssBaseline from "@mui/material/CssBaseline";
import { TanStackDevtools } from "@tanstack/react-devtools";
import Home from "./Home";
import React from "react";
import { BrowserRouter, Route, Routes } from "react-router";
import { ThemeProvider } from "@mui/material/styles";
import { createAxiosClient } from "@ntg/component-library/utilities";
import { createThemeFromSeed } from "@ntg/component-library/theming";
import { ReactQueryDevtoolsPanel } from "@tanstack/react-query-devtools";
import { useQuery } from "@tanstack/react-query";
import {
    LayoutContextProvider,
    UserContextProvider,
    useIsInRole,
} from "@ntg/component-library/contexts";
import { DesktopLayout } from "@ntg/component-library/app";
import { AnimatePresence } from "motion/react";
import { getDrawerContents } from "./DrawerContents";
import ProcessMonitorPage from "./monitor/ProcessMonitorPage";
import SalesOrderSearchContainer from "./sales/SalesOrderSearchContainer";
import ServiceOrderSearchContainer from "./service/ServiceOrderSearchContainer";
import ManufacturingContainer from "./manufacturing/ManufacturingSearchContainer";
import ManufacturingDetailContainer from "./manufacturing/ManufacturingDetailContainer";
import SalesOrderContainer from "./sales/SalesOrderContainer";
import ServiceOrderDetailContainer from "./service/ServiceOrderDetailContainer";
import DataExplorerPage from "./explorer/DataExplorerPage";
import ODataExplorerPage from "./odataexplorer/ODataExplorerPage";
import { useProcessMonitorStore } from "./stores/useProcessMonitorStore";
export default function App() {
    const axiosAuth = createAxiosClient("Auth");

    const { isLoading, error } = useQuery({
        queryFn: async () => {
            const response = await axiosAuth.get("/Auth/validate");
            return response.data;
        },
        queryKey: ["validate"],
    });

    if (isLoading) return "Authenticating...";

    if (error) return "An error has occurred: " + error.message;

    const isDarkMode = window.matchMedia?.("(prefers-color-scheme: dark)").matches ?? false;
    const theme = createThemeFromSeed({ menuBar: "rgb(37, 51, 80)", isDarkMode });

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <TanStackDevtools
                plugins={[
                    {
                        name: "TanStack Query",
                        render: <ReactQueryDevtoolsPanel />,
                        defaultOpen: true,
                    },
                ]}
            />
            <UserContextProvider>
                <BrowserRouter>
                    <AppLayout />
                </BrowserRouter>
            </UserContextProvider>
        </ThemeProvider>
    );
}

function AppLayout() {
    const activeCount = useProcessMonitorStore((state) => state.activeCount);
    const { IsInRole: isGlobalAdmin } = useIsInRole("Global Admin");

    return (
        <LayoutContextProvider
            DrawerContents={getDrawerContents(activeCount, isGlobalAdmin === true)}
            ModuleName={"FinanceAndOperations"}
            ModuleTitle={"D365 F&O"}
        >
            <DesktopLayout
                MainContent={
                    <AnimatePresence mode="wait">
                        <Routes>
                            <Route element={<Home />} path="/" />
                            <Route element={<Home />} path="/home" />
                            <Route
                                element={<SalesOrderSearchContainer />}
                                path="/salesorder"
                            />
                            <Route
                                path="/SalesOrderHeader/:id"
                                element={<SalesOrderContainer />}
                            >
                                <Route
                                    path="customer"
                                    element={null}
                                />
                                <Route
                                    path="header"
                                    element={null}
                                />
                                <Route
                                    path="vatnum"
                                    element={null}
                                />
                                <Route
                                    path="headercharge"
                                    element={null}
                                />
                                <Route
                                    path="commit/:instanceId"
                                    element={null}
                                />
                            </Route>
                            <Route
                                element={
                                    <div>
                                        <ServiceOrderSearchContainer />
                                    </div>
                                }
                                path="/serviceorder"
                            />
                            <Route
                                path="/ServiceOrderHeader/:id"
                                element={<ServiceOrderDetailContainer />}
                            >
                                <Route
                                    path="commit/:instanceId"
                                    element={null}
                                />
                            </Route>
                            <Route
                                element={<ManufacturingContainer />}
                                path="/manufacturing"
                            />
                            <Route
                                path="/ManufacturingItem/:id"
                                element={<ManufacturingDetailContainer />}
                            >
                                <Route
                                    path="commit/:instanceId"
                                    element={null}
                                />
                            </Route>
                            <Route
                                element={<ODataExplorerPage />}
                                path="/odata-explorer"
                            />
                            <Route
                                element={<DataExplorerPage />}
                                path="/explorer/:entryType/:id"
                            />
                            <Route
                                path="/monitor"
                                element={<ProcessMonitorPage />}
                            >
                                <Route
                                    path=":instanceId"
                                    element={null}
                                />
                            </Route>
                        </Routes>
                    </AnimatePresence>
                }
                TerminalContent={<div></div>}
            />
        </LayoutContextProvider>
    );
}
